import {
  Component,
  ElementRef,
  Input,
  OnInit,
  forwardRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validators,
  Validator,
  ValidationErrors,
  FormGroup,
  FormBuilder,
  AbstractControl,
} from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';
import { TimeField } from './TimeField';

@Component({
  selector: 'app-time-field',
  templateUrl: './time-field.component.html',
  styleUrls: ['./time-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => TimeFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => TimeFieldComponent),
      multi: true,
    },
  ],
})
export class TimeFieldComponent
  implements OnInit, ControlValueAccessor, Validator, OnDestroy
{
  @ViewChild('hour') hourInput: HTMLInputElement;
  @ViewChild('minute') minuteInput: HTMLInputElement;
  @ViewChild('ampm') ampmInput: HTMLInputElement;
  parts: FormGroup;
  stateChanges = new Subject<void>();
  focused = false;
  touched = false;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: any = () => {};

  // @Input()
  // get placeholder(): string {
  //   return this._placeholder;
  // }
  // set placeholder(value: string) {
  //   this._placeholder = value;
  //   this.stateChanges.next();
  // }
  // private _placeholder: string;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    if (typeof this.parts !== 'undefined') {
      this._disabled ? this.parts.disable() : this.parts.enable();
    }
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): TimeField | null {
    if (this.parts.valid) {
      const {
        value: { hour, minute, ampm },
      } = this.parts;
      return new TimeField(hour, minute, ampm);
    }
    return null;
  }
  set value(timeField: TimeField | null) {
    const { hour, minute, ampm } = timeField || new TimeField(12, 1, 'am');
    this.parts.setValue({ hour, minute, ampm });
    this.stateChanges.next();
  }

  constructor(
    private _formBuilder: FormBuilder,
    private _elementRef: ElementRef<HTMLElement>,
    private _focusMonitor: FocusMonitor
  ) {}

  ngOnInit(): void {
    this.parts = this._formBuilder.group({
      hour: [
        '',
        [
          Validators.required,
          Validators.min(1),
          Validators.max(12),
          Validators.minLength(2),
          Validators.maxLength(2),
        ],
      ],
      minute: [
        '',
        [
          Validators.required,
          Validators.min(0),
          Validators.max(59),
          Validators.minLength(2),
          Validators.maxLength(2),
        ],
      ],
      ampm: ['', [Validators.required, Validators.pattern('[AaPp][Mm]')]],
    });
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  onContainerClick() {
    const origin = 'program';
    if (this.parts.controls.ampm.valid) {
      this._focusMonitor.focusVia(this.ampmInput, origin);
    } else if (this.parts.controls.minute.valid) {
      this._focusMonitor.focusVia(this.ampmInput, origin);
    } else if (this.parts.controls.hour.valid) {
      this._focusMonitor.focusVia(this.minuteInput, origin);
    } else {
      this._focusMonitor.focusVia(this.hourInput, origin);
    }
  }

  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (
      !this._elementRef.nativeElement.contains(event.relatedTarget as Element)
    ) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  autoFocusNext(
    control: AbstractControl,
    nextElement?: HTMLInputElement
  ): void {
    if (!control.errors && nextElement) {
      this._focusMonitor.focusVia(nextElement, 'program');
    }
  }

  autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
    if (control.value.length < 1) {
      this._focusMonitor.focusVia(prevElement, 'program');
    }
  }

  handleInput(control: AbstractControl, nextElement?: HTMLInputElement): void {
    this.autoFocusNext(control, nextElement);
    this.onChange(this.value);
  }

  writeValue(timeField: TimeField | null): void {
    this.value = timeField;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get errorState(): boolean {
    return this.parts.invalid && this.touched;
  }

  // registerOnValidatorChange(fn: any): void {}

  validate(_: AbstractControl): ValidationErrors | null {
    if (this.parts.valid) {
      return null;
    }

    let errors: any = {};

    errors = this.addControlErrors(errors, 'hour');
    errors = this.addControlErrors(errors, 'minute');
    errors = this.addControlErrors(errors, 'ampm');

    return errors;
  }

  addControlErrors(allErrors: any, controlName: string) {
    const errors = { ...allErrors };
    const controlErrors = this.parts.controls[controlName].errors;
    if (controlErrors) {
      errors[controlName] = controlErrors;
    }
    return errors;
  }
}
